import React, { useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';

import { queryStrings } from '@trqk/resolvers';
import { useIntersectionObserver } from '@trqk/hooks';
import { LeftNavContentSection, LeftNavHeader } from '@trqk/components';

import { WebLayout } from '../../templates';
import {
    AccountHeaderContent,
    EntityManager,
    InviteOthers,
    Notifications,
    PaymentPlan,
    Profile,
} from './components';
import { sections, sectionsWithPaywall } from './constants';
import { MAIN_CONTENT_ID } from '../../../constants';
import { getFeatureFlags } from '../../../featureFlags';

const { GET_SESSION_CURRENT_USER } = queryStrings;

/**
 * Displays user account management tools, including Profile, Entity Manager, Invite Others, and
 * License. Currently, License is only shown if enabled by the 'paywall' feature flag.
 */
export const Settings = (): JSX.Element => {
    const { paywall } = getFeatureFlags();
    const menuSections = paywall ? sectionsWithPaywall : sections;
    const sectionIds = menuSections.map(({ id }: HeaderSection): string => id);
    const sessionQuery = useQuery(GET_SESSION_CURRENT_USER);
    const currentUser = sessionQuery?.data?.session?.currentUser ?? null;
    const userId = currentUser?.id ?? null;
    const history = useHistory();

    const onSetActiveSectionId = useCallback(
        (id: string) => {
            if (history.location.hash !== id) {
                history.replace({
                    search: history.location.search,
                    hash: id,
                });
            }
        },
        [history.location.hash, userId],
    );

    const { activeSectionId, observe } = useIntersectionObserver({
        defaultSectionId: menuSections[0].id,
        sectionIds,
        observerOptions: {
            root: document.querySelector(MAIN_CONTENT_ID),
            rootMargin: '0px',
            threshold: 0.7,
        },
        onSetActiveSectionId,
    });

    return (
        <WebLayout
            collapsedHeaderPadding="sm"
            headerPadding="xl"
            loading={sessionQuery.loading}
            header={{
                content: (
                    <LeftNavHeader
                        activeSectionId={activeSectionId}
                        sections={menuSections}
                    >
                        <AccountHeaderContent currentUser={currentUser} />
                    </LeftNavHeader>
                ),
                wrapperClass: 'account-header',
            }}
        >
            <div className="flex flex-col w-full">
                {currentUser !== null && (
                    <div className="mb-10">
                        <LeftNavContentSection
                            id={menuSections[0].id}
                            ref={observe}
                        >
                            <Profile currentUser={currentUser} />
                        </LeftNavContentSection>
                        <LeftNavContentSection
                            id={menuSections[1].id}
                            ref={observe}
                        >
                            <EntityManager />
                        </LeftNavContentSection>
                        <LeftNavContentSection
                            id={menuSections[2].id}
                            ref={observe}
                        >
                            <InviteOthers currentUser={currentUser} />
                        </LeftNavContentSection>
                        <LeftNavContentSection
                            id={menuSections[3].id}
                            ref={observe}
                        >
                            <Notifications userId={userId} />
                        </LeftNavContentSection>
                        {paywall && (
                            <LeftNavContentSection
                                id={menuSections[4].id}
                                ref={observe}
                            >
                                <PaymentPlan userId={userId} />
                            </LeftNavContentSection>
                        )}
                    </div>
                )}
            </div>
        </WebLayout>
    );
};
